/**
 * Native primitives wrapper — Mobile M2 slice.
 *
 * Surfaces a few thin hooks/helpers around Capacitor plugins so the rest
 * of the app can reach for "take photo" / "online status" / biometric
 * unlock without importing Capacitor directly. Each helper degrades
 * gracefully on web (returns `null` / native:false / no-op) so callsites
 * can be shared between the web bundle and the iOS/Android Capacitor wrap.
 *
 * Plugins wired so far:
 *   - @capacitor/camera             → takePhoto({source, quality}), pickPhotos() multi-select
 *   - @capacitor/network            → useNetworkStatus() React hook
 *   - @aparajita/capacitor-biometric-auth → biometric unlock
 *   - @capacitor/preferences        → preference storage for biometric-enabled flag + token
 *   - @capacitor/push-notifications → registerPushDevice / push listeners
 *
 * The push primitive registers an APNs (iOS) or FCM (Android) token
 * with the backend `device_tokens` table; the actual fan-out from
 * server-side events to that token is gated on DT4's notification-
 * event catalog and lives in `internal/api/push_native_handlers.go`.
 */
import { useEffect, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera'
import { Network } from '@capacitor/network'
import { BiometricAuth, BiometryType } from '@aparajita/capacitor-biometric-auth'
import { Preferences } from '@capacitor/preferences'
import {
  PushNotifications,
  type PermissionStatus,
  type Token,
  type PushNotificationSchema,
  type ActionPerformed,
} from '@capacitor/push-notifications'

export function isNativePlatform(): boolean {
  try {
    return Capacitor.isNativePlatform()
  } catch {
    return false
  }
}

// Apple App Store Guideline 3.1.1 — payment surfaces (signup with card,
// plan picker, upgrade CTAs) must not render in the iOS Capacitor build
// since Tenon doesn't ship StoreKit IAP. Use this gate, not
// isNativePlatform(), so Android still gets the full payment UX.
export function isIOS(): boolean {
  try {
    return Capacitor.getPlatform() === 'ios'
  } catch {
    return false
  }
}

// AUTH-02c (#1520): Android-only capability gates (e.g. the verified App Link
// OAuth callback — iOS uses ASWebAuthenticationSession and never needs it).
export function isAndroid(): boolean {
  try {
    return Capacitor.getPlatform() === 'android'
  } catch {
    return false
  }
}

export interface TakePhotoOptions {
  /** "camera" forces the camera UI; "photos" opens the photo library;
   *  "prompt" lets the OS choose. Defaults to "prompt" so iOS gets the
   *  native action sheet (Take Photo / Choose from Library / Cancel). */
  source?: 'camera' | 'photos' | 'prompt'
  /** JPEG quality 0-100. Defaults to 85 — balances image quality against
   *  upload size for site photos. */
  quality?: number
}

export interface TakePhotoResult {
  /** Object URL for preview (release with URL.revokeObjectURL). */
  previewUrl: string
  /** File payload that can be appended to FormData and POSTed. */
  file: File
}

const SOURCE_MAP: Record<NonNullable<TakePhotoOptions['source']>, CameraSource> = {
  camera: CameraSource.Camera,
  photos: CameraSource.Photos,
  prompt: CameraSource.Prompt,
}

/**
 * takePhoto — opens the native camera/library picker, returns the result
 * as a File ready to POST. Throws on cancel or permission denial; the
 * caller's catch should treat both as "user changed mind".
 */
export async function takePhoto(opts: TakePhotoOptions = {}): Promise<TakePhotoResult> {
  if (!isNativePlatform()) {
    throw new Error('takePhoto is only available in the native mobile app')
  }
  const source = opts.source || 'prompt'
  const quality = opts.quality ?? 85
  const photo = await Camera.getPhoto({
    quality,
    allowEditing: false,
    resultType: CameraResultType.DataUrl,
    source: SOURCE_MAP[source],
  })
  if (!photo.dataUrl) {
    throw new Error('camera returned no data')
  }
  const blob = dataURLtoBlob(photo.dataUrl)
  const ext = (photo.format || 'jpeg').toLowerCase()
  const filename = `photo-${Date.now()}.${ext === 'jpg' ? 'jpeg' : ext}`
  const file = new File([blob], filename, { type: blob.type })
  return {
    previewUrl: URL.createObjectURL(blob),
    file,
  }
}

/**
 * pickPhotos — opens the native photo library with multi-select and returns
 * every chosen photo as a File ready to POST. `getPhoto` (used by takePhoto)
 * can only ever return one image, so bulk-attaching site photos needs this.
 * Uses `pickImages` (available since Camera 1.x) rather than 8.1's
 * `chooseFromGallery` so an OTA bundle stays safe on older native binaries.
 * Throws on cancel or permission denial, like takePhoto.
 */
export async function pickPhotos(opts: { quality?: number; limit?: number } = {}): Promise<File[]> {
  if (!isNativePlatform()) {
    throw new Error('pickPhotos is only available in the native mobile app')
  }
  const { photos } = await Camera.pickImages({ quality: opts.quality ?? 85, limit: opts.limit ?? 0 })
  const files: File[] = []
  for (const [i, p] of photos.entries()) {
    const blob = await (await fetch(p.webPath)).blob()
    const ext = (p.format || 'jpeg').toLowerCase()
    const type = blob.type && blob.type !== 'application/octet-stream' ? blob.type : `image/${ext === 'jpg' ? 'jpeg' : ext}`
    files.push(new File([blob], `photo-${Date.now()}-${i}.${ext === 'jpg' ? 'jpeg' : ext}`, { type }))
  }
  return files
}

function dataURLtoBlob(dataURL: string): Blob {
  const parts = dataURL.split(',')
  const meta = parts[0]
  const base64 = parts[1] || ''
  const mimeMatch = meta.match(/:([^;]+)/)
  const mime = mimeMatch ? mimeMatch[1] : 'application/octet-stream'
  const binary = atob(base64)
  const len = binary.length
  const bytes = new Uint8Array(len)
  for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i)
  return new Blob([bytes], { type: mime })
}

/**
 * useNetworkStatus — subscribes to @capacitor/network status changes
 * (and also navigator.onLine on web for graceful degrade). Returns
 * { connected, connectionType }. Renders true on web by default; the
 * value flips when navigator emits offline events.
 */
export interface NetworkStatus {
  connected: boolean
  connectionType: string
}

// ── Durable auth persistence (survives app updates) ─────────────────────
//
// The auth token, refresh token, and trusted-device (2FA "remember this
// device") token all live in WKWebView localStorage, which iOS can wipe on an
// app update — logging the user out and re-triggering 2FA after every update
// (Joe report 2026-06-23). Capacitor Preferences is native key-value storage
// that DOES survive updates, so we mirror the auth keys there on every change
// and restore them into localStorage on boot when WKWebView came up empty.
// ml_token_original (#2141): holds the admin's own token while impersonating
// someone else (ImpersonationBanner) — the same durability need as ml_token
// itself, or an app-kill mid-impersonation restores as the impersonated user
// with no "Exit" swap-back available (ml_token_original gone) until the
// short-lived impersonated token expires and the intact admin refresh token
// silently recovers it an hour later.
const AUTH_KEYS = ['ml_token', 'ml_refresh_token', 'ml_token_exp', 'tenon_trusted_device', 'ml_token_original'] as const

/** Copy the current localStorage auth keys into durable Preferences. No-op off-native. */
export async function mirrorAuthToPreferences(): Promise<void> {
  if (!isNativePlatform()) return
  try {
    for (const k of AUTH_KEYS) {
      const v = localStorage.getItem(k)
      if (v) await Preferences.set({ key: 'auth.' + k, value: v })
      else await Preferences.remove({ key: 'auth.' + k })
    }
  } catch { /* preferences unavailable — best effort */ }
}

/** Reads a single durable auth key straight from Preferences (bypassing
 *  localStorage) — used as the belt-and-suspenders fallback when a refresh
 *  needs a token that WKWebView evicted from localStorage mid-session. */
export async function getPreference(authKey: (typeof AUTH_KEYS)[number]): Promise<{ value: string | null }> {
  if (!isNativePlatform()) return { value: null }
  try {
    const { value } = await Preferences.get({ key: 'auth.' + authKey })
    return { value: value || null }
  } catch {
    return { value: null }
  }
}

/** Restore auth keys from durable Preferences into localStorage when the
 *  webview lost them (e.g. after an app update). Only fills empties — never
 *  overwrites a live value. Returns true if it restored the access token. */
export async function restoreAuthFromPreferences(): Promise<boolean> {
  if (!isNativePlatform()) return false
  // Just signed out intentionally (#3082) — do not revive the session from
  // durable storage. Cleared by setToken() on the next real sign-in.
  if (localStorage.getItem('intentional_signout') === '1') return false
  let restoredToken = false
  try {
    for (const k of AUTH_KEYS) {
      if (localStorage.getItem(k)) continue
      const { value } = await Preferences.get({ key: 'auth.' + k })
      if (value) {
        localStorage.setItem(k, value)
        if (k === 'ml_token') restoredToken = true
      }
    }
  } catch { /* best effort */ }
  return restoredToken
}

/** Durable, awaited sign-out (#3082): remove every auth key from Preferences
 *  plus the biometric-stored token, so no revival path (getRefreshToken's
 *  Preferences fallback, restoreAuthFromPreferences, biometric unlock) can bring
 *  the session back after the user taps Sign Out. No-op off-native. */
export async function clearAuthPreferences(): Promise<void> {
  if (!isNativePlatform()) return
  try {
    for (const k of AUTH_KEYS) {
      await Preferences.remove({ key: 'auth.' + k })
    }
    await Preferences.remove({ key: PREF_BIOMETRIC_TOKEN })
    await Preferences.remove({ key: PREF_BIOMETRIC_ENABLED })
  } catch { /* best effort — localStorage is already cleared by clearToken() */ }
}

// ── Biometric unlock ────────────────────────────────────────────────────

const PREF_BIOMETRIC_ENABLED = 'tenon.biometric_enabled'
const PREF_BIOMETRIC_TOKEN = 'tenon.biometric_token'

export interface BiometricCapability {
  available: boolean
  type: 'touchId' | 'faceId' | 'fingerprint' | 'face' | 'iris' | 'none'
}

const BIOMETRY_LABELS: Record<BiometryType, BiometricCapability['type']> = {
  [BiometryType.none]: 'none',
  [BiometryType.touchId]: 'touchId',
  [BiometryType.faceId]: 'faceId',
  [BiometryType.fingerprintAuthentication]: 'fingerprint',
  [BiometryType.faceAuthentication]: 'face',
  [BiometryType.irisAuthentication]: 'iris',
}

/** Returns whether biometric hardware is available + which kind. */
export async function checkBiometricCapability(): Promise<BiometricCapability> {
  if (!isNativePlatform()) return { available: false, type: 'none' }
  try {
    const r = await BiometricAuth.checkBiometry()
    return {
      available: !!r.isAvailable,
      type: BIOMETRY_LABELS[r.biometryType] || 'none',
    }
  } catch {
    return { available: false, type: 'none' }
  }
}

export async function isBiometricEnabled(): Promise<boolean> {
  if (!isNativePlatform()) return false
  try {
    const { value } = await Preferences.get({ key: PREF_BIOMETRIC_ENABLED })
    return value === '1'
  } catch {
    return false
  }
}

/**
 * Enable biometric unlock — prompts the user once to authenticate, then
 * stores the REFRESH token under a biometric-protected preference and the
 * "enabled" flag. The preference is read back next launch via
 * `unlockWithBiometric()`, which exchanges it for a fresh access token.
 */
export async function enableBiometricUnlock(refreshToken: string, reason = 'Enable biometric unlock'): Promise<boolean> {
  if (!isNativePlatform()) return false
  try {
    await BiometricAuth.authenticate({ reason })
    await Preferences.set({ key: PREF_BIOMETRIC_TOKEN, value: refreshToken })
    await Preferences.set({ key: PREF_BIOMETRIC_ENABLED, value: '1' })
    return true
  } catch {
    return false
  }
}

/** Disables biometric unlock and wipes the stored token. */
export async function disableBiometricUnlock(): Promise<void> {
  if (!isNativePlatform()) return
  try {
    await Preferences.remove({ key: PREF_BIOMETRIC_TOKEN })
    await Preferences.remove({ key: PREF_BIOMETRIC_ENABLED })
  } catch {}
}

/** Updates the stored biometric refresh token when a new refresh token is issued (via token rotation). */
export async function updateBiometricRefreshToken(refreshToken: string): Promise<void> {
  if (!isNativePlatform()) return
  if (!(await isBiometricEnabled())) return
  try {
    await Preferences.set({ key: PREF_BIOMETRIC_TOKEN, value: refreshToken })
  } catch {}
}

/**
 * On app launch, if the user has previously enabled biometric unlock,
 * prompts for biometric and returns the stored refresh token. Caller
 * exchanges it for a fresh access token via /auth/refresh.
 */
export async function unlockWithBiometric(reason = 'Sign in to Tenon'): Promise<string | null> {
  if (!isNativePlatform()) return null
  if (!(await isBiometricEnabled())) return null
  try {
    await BiometricAuth.authenticate({ reason })
    const { value } = await Preferences.get({ key: PREF_BIOMETRIC_TOKEN })
    return value || null
  } catch {
    return null
  }
}

// ── Network status (existing) ───────────────────────────────────────────

export function useNetworkStatus(): NetworkStatus {
  const [status, setStatus] = useState<NetworkStatus>(() => ({
    connected: typeof navigator === 'undefined' ? true : navigator.onLine !== false,
    connectionType: 'unknown',
  }))

  useEffect(() => {
    let cancelled = false
    let removeListener: (() => void) | null = null

    Network.getStatus().then((s) => {
      if (cancelled) return
      setStatus({ connected: s.connected, connectionType: s.connectionType })
    }).catch(() => {
      // Web fallback — Network plugin works on browser too but be defensive
    })

    Network.addListener('networkStatusChange', (s) => {
      if (cancelled) return
      setStatus({ connected: s.connected, connectionType: s.connectionType })
    }).then((handle) => {
      removeListener = () => { handle.remove() }
    }).catch(() => {})

    const onOnline = () => setStatus((prev) => ({ ...prev, connected: true }))
    const onOffline = () => setStatus((prev) => ({ ...prev, connected: false }))
    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)

    return () => {
      cancelled = true
      if (removeListener) removeListener()
      window.removeEventListener('online', onOnline)
      window.removeEventListener('offline', onOffline)
    }
  }, [])

  return status
}

// ── Push notifications ──────────────────────────────────────────────────

const PREF_PUSH_TOKEN = 'tenon.push_token'

export type PushPlatform = 'ios' | 'android'

/**
 * Returns the platform string the backend stores in `device_tokens.platform`.
 * `null` when not running natively (web bundle has no APNs/FCM token).
 */
export function pushPlatform(): PushPlatform | null {
  if (!isNativePlatform()) return null
  const p = Capacitor.getPlatform()
  if (p === 'ios') return 'ios'
  if (p === 'android') return 'android'
  return null
}

/**
 * Asks the OS for permission to display push notifications. Returns
 * `true` when granted. Caller should only ever invoke this after a
 * user-visible opt-in (e.g. settings toggle), per Apple's HIG.
 */
export async function requestPushPermission(): Promise<boolean> {
  if (!isNativePlatform()) return false
  try {
    const status: PermissionStatus = await PushNotifications.requestPermissions()
    return status.receive === 'granted'
  } catch {
    return false
  }
}

/**
 * Returns the most recently-registered push token (cached in
 * `@capacitor/preferences`) so callers can avoid re-running registration
 * on every app launch.
 */
export async function getCachedPushToken(): Promise<string | null> {
  if (!isNativePlatform()) return null
  try {
    const { value } = await Preferences.get({ key: PREF_PUSH_TOKEN })
    return value || null
  } catch {
    return null
  }
}

/**
 * Registers the device with APNs (iOS) or FCM (Android) and resolves
 * with the token string. Internally adds a one-shot `registration`
 * listener and waits up to ~10s for the OS callback. Resolves `null`
 * on permission denial, registration error, or timeout. The token is
 * cached so subsequent calls short-circuit to `getCachedPushToken()`.
 */
export async function registerPushDevice(): Promise<string | null> {
  if (!isNativePlatform()) return null
  const granted = await requestPushPermission()
  if (!granted) return null

  return new Promise<string | null>((resolve) => {
    let settled = false
    const settle = (val: string | null) => {
      if (settled) return
      settled = true
      resolve(val)
    }

    const timer = setTimeout(() => settle(null), 10000)

    PushNotifications.addListener('registration', async (token: Token) => {
      try {
        await Preferences.set({ key: PREF_PUSH_TOKEN, value: token.value })
      } catch {}
      clearTimeout(timer)
      settle(token.value)
    }).catch(() => settle(null))

    PushNotifications.addListener('registrationError', () => {
      clearTimeout(timer)
      settle(null)
    }).catch(() => {})

    PushNotifications.register().catch(() => {
      clearTimeout(timer)
      settle(null)
    })
  })
}

/**
 * Subscribes a foreground callback for incoming push payloads. Returns
 * an unsubscribe function. Callers typically use this to surface an
 * in-app toast / inbox refresh — the OS handles the notification UI.
 */
export async function addPushReceivedListener(
  handler: (n: PushNotificationSchema) => void
): Promise<() => void> {
  if (!isNativePlatform()) return () => {}
  const handle = await PushNotifications.addListener('pushNotificationReceived', handler)
  return () => { handle.remove() }
}

/**
 * Subscribes a tap-to-open callback (user tapped the notification in
 * the OS notification center). Use to navigate to the relevant deep
 * link. Returns an unsubscribe function.
 */
export async function addPushActionListener(
  handler: (a: ActionPerformed) => void
): Promise<() => void> {
  if (!isNativePlatform()) return () => {}
  const handle = await PushNotifications.addListener('pushNotificationActionPerformed', handler)
  return () => { handle.remove() }
}

/** Removes the cached push token and unregisters all listeners. */
export async function unregisterPushDevice(): Promise<void> {
  if (!isNativePlatform()) return
  try {
    await PushNotifications.removeAllListeners()
  } catch {}
  try {
    await Preferences.remove({ key: PREF_PUSH_TOKEN })
  } catch {}
}
